import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Rating,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Snackbar,
  Grid,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import StarIcon from '@mui/icons-material/Star';
import ShareIcon from '@mui/icons-material/Share';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface ProfileData {
  referralCode?: string;
  freeLessonCredits?: number;
  referralCampaignEnabled?: boolean;
}

const ReferralAndReviews: React.FC = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [campaignEnabled, setCampaignEnabled] = useState(true);
  
  // Feedback form state
  const [rating, setRating] = useState<number | null>(5);
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  // Clipboard notification
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      if (user?.role === 'Instructor') {
        const response = await api.getInstructorProfile();
        // Instructor profiles can also fetch from getMyInstructorProfile
        const profileResponse = await api.getMyInstructorProfile();
        setProfile({
          referralCode: profileResponse.data.referralCode,
          referralCampaignEnabled: profileResponse.data.referralCampaignEnabled,
        });
        setCampaignEnabled(profileResponse.data.referralCampaignEnabled ?? true);
      } else {
        const response = await api.getStudentProfile();
        setProfile({
          referralCode: response.data.referralCode,
          freeLessonCredits: response.data.freeLessonCredits,
        });
      }
    } catch (error) {
      console.error('Error fetching profile for referrals:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, [user]);

  const handleCopyCode = () => {
    if (profile?.referralCode) {
      navigator.clipboard.writeText(profile.referralCode);
      setSnackbarMessage('Referral code copied to clipboard!');
      setSnackbarOpen(true);
    }
  };

  const handleToggleCampaign = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setCampaignEnabled(checked);
    try {
      if (user?.role === 'Instructor') {
        // Load existing profile first to preserve other fields
        const profileResponse = await api.getMyInstructorProfile();
        const existing = profileResponse.data;
        
        await api.updateMyInstructorProfile({
          bio: existing.bio || existing.profileInfo,
          baseHourlyRate: existing.baseHourlyRate,
          transmissionType: existing.transmissionType,
          outcodes: existing.outcodes,
          licenseTypes: existing.licenseTypes,
          vehicleImageUrls: existing.vehicleImageUrls,
          logoUrl: existing.logoUrl,
          referralCampaignEnabled: checked,
        } as any);

        setSnackbarMessage(
          checked 
            ? 'Referral campaign enabled. Your students can now earn rewards!' 
            : 'Referral campaign disabled. Referral rewards are paused.'
        );
        setSnackbarOpen(true);
        // Refresh local data
        setProfile(prev => prev ? { ...prev, referralCampaignEnabled: checked } : null);
      }
    } catch (error) {
      console.error('Error toggling campaign:', error);
      setCampaignEnabled(!checked); // Rollback UI state
      setSnackbarMessage('Failed to update referral campaign settings.');
      setSnackbarOpen(true);
    }
  };

  const handleSubmitFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rating || !content.trim()) return;

    try {
      setSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);

      await api.submitTestimonial({
        rating,
        content: content.trim()
      });

      setSubmitSuccess(true);
      setContent('');
      setRating(5);
    } catch (error: any) {
      console.error('Error submitting testimonial:', error);
      setSubmitError(error.response?.data?.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress size={40} />
      </Box>
    );
  }

  const referralLink = `${window.location.origin}/register?ref=${profile?.referralCode || ''}`;

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      <Grid container spacing={3}>
        {/* Referral Program Section */}
        <Grid size={{ xs: 12 }}>
          <Card 
            elevation={3} 
            sx={{ 
              borderRadius: 3, 
              background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
              color: 'white',
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.2)' 
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <ShareIcon sx={{ fontSize: 32, mr: 1.5, color: '#fdd835' }} />
                <Typography variant="h5" fontWeight="bold">
                  Referral Program
                </Typography>
              </Box>

              {user?.role === 'Student' ? (
                <>
                  <Typography variant="body1" sx={{ opacity: 0.9, mb: 3 }}>
                    Share your unique referral code with your friends. When they register and complete their first driving lesson under an eligible instructor, you'll earn <strong>1 Free Driving Lesson Credit</strong>!
                  </Typography>

                  <Box display="flex" alignItems="center" sx={{ backgroundColor: 'rgba(255,255,255,0.1)', p: 1.5, borderRadius: 2, mb: 3 }}>
                    <Typography variant="caption" sx={{ display: 'block', opacity: 0.7, mr: 2, fontWeight: 'bold' }}>
                      YOUR CODE:
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'monospace', letterSpacing: 1.5, flexGrow: 1 }}>
                      {profile?.referralCode || 'STU-XXXX'}
                    </Typography>
                    <Button 
                      variant="contained" 
                      color="secondary" 
                      startIcon={<ContentCopyIcon />}
                      onClick={handleCopyCode}
                      sx={{ 
                        textTransform: 'none', 
                        backgroundColor: '#fdd835', 
                        color: '#000',
                        '&:hover': { backgroundColor: '#fbc02d' }
                      }}
                    >
                      Copy Code
                    </Button>
                  </Box>

                  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ backgroundColor: 'rgba(255,255,255,0.05)', p: 2, borderRadius: 2 }}>
                    <Box display="flex" alignItems="center">
                      <CheckCircleIcon sx={{ mr: 1, color: '#4caf50' }} />
                      <Typography variant="subtitle1" fontWeight="medium">
                        Your Free Lesson Credits:
                      </Typography>
                    </Box>
                    <Typography variant="h4" fontWeight="bold" color="#fdd835">
                      {profile?.freeLessonCredits ?? 0}
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <Typography variant="body1" sx={{ opacity: 0.9, mb: 3 }}>
                    Share your referral code with fellow instructors. When a new instructor registers using your code, your active subscription is instantly extended by <strong>30 days</strong>!
                  </Typography>

                  <Box display="flex" alignItems="center" sx={{ backgroundColor: 'rgba(255,255,255,0.1)', p: 1.5, borderRadius: 2, mb: 3 }}>
                    <Typography variant="caption" sx={{ display: 'block', opacity: 0.7, mr: 2, fontWeight: 'bold' }}>
                      REFERRAL CODE:
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'monospace', letterSpacing: 1.5, flexGrow: 1 }}>
                      {profile?.referralCode || 'INS-XXXX'}
                    </Typography>
                    <Button 
                      variant="contained" 
                      color="secondary" 
                      startIcon={<ContentCopyIcon />}
                      onClick={handleCopyCode}
                      sx={{ 
                        textTransform: 'none', 
                        backgroundColor: '#fdd835', 
                        color: '#000',
                        '&:hover': { backgroundColor: '#fbc02d' }
                      }}
                    >
                      Copy Code
                    </Button>
                  </Box>

                  <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.2)', my: 3 }} />

                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                        Student Referral Discount Campaign
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8, maxWidth: 500 }}>
                        If enabled, students who are referred to you will be eligible for free lesson rewards upon completing their first lesson, sponsored by your dashboard program.
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={campaignEnabled} 
                          onChange={handleToggleCampaign} 
                          color="secondary"
                        />
                      }
                      label={campaignEnabled ? "Campaign Active" : "Campaign Paused"}
                      sx={{ color: 'white' }}
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Platform Feedback Section */}
        <Grid size={{ xs: 12 }}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Platform Testimonial
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                We'd love to hear about your experience with our driving school management platform! Write a brief testimonial below. Once approved by an admin, it will be highlighted on our landing page.
              </Typography>

              {submitSuccess && (
                <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                  Thank you! Your feedback has been submitted successfully and is currently pending administrator moderation.
                </Alert>
              )}

              {submitError && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {submitError}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmitFeedback} noValidate sx={{ mt: 2 }}>
                <Box display="flex" alignItems="center" mb={3}>
                  <Typography component="legend" variant="subtitle1" fontWeight="medium" sx={{ mr: 2 }}>
                    Your Rating:
                  </Typography>
                  <Rating
                    name="platform-rating"
                    value={rating}
                    onChange={(event, newValue) => {
                      setRating(newValue);
                    }}
                    precision={1}
                    size="large"
                    emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                  />
                </Box>

                <TextField
                  id="feedback-content"
                  label="Your Testimonial Content"
                  multiline
                  rows={4}
                  variant="outlined"
                  fullWidth
                  required
                  placeholder="Share details about what you like most, how it helped your journey, or features you love..."
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  sx={{ mb: 3 }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={submitting || !content.trim()}
                  sx={{ 
                    py: 1.2, 
                    px: 4, 
                    borderRadius: 2, 
                    textTransform: 'none', 
                    fontWeight: 'bold',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)'
                  }}
                >
                  {submitting ? 'Submitting...' : 'Submit Testimonial'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Snackbar alerts */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default ReferralAndReviews;
